import GenericBadge, { BadgeProps } from "@ui-components/Badge/GenericBadge";
import GenericButton, { GenericButtonProps } from "@ui-components/Button/GenericButton";
import SegmentedControl, { SegmentedControlProps } from "@ui-components/SegmentedContol/GenericSegmentedContol";
import React, { useState, useRef, useEffect } from "react";

/** Available dropdown sizes. */
export type DropDownSize = "sm" | "md" | "lg";

/**
 * - `sm` => h-8 (32px)
 * - `md` => ~40px (if no custom height is provided)
 * - `lg` => 48px
 */
const sizeClasses: Record<DropDownSize, string> = {
  sm: "h-8 px-2 gap-1 text-sm",
  md: "py-1.5 px-3 gap-2 text-sm",
  lg: "h-12 px-4 gap-2 text-base",
};

export interface GenericDropdownProps {
  /**
   * Array de strings a mostrar en el dropdown.
   */
  items: string[];
  /**
   * Texto por defecto antes de seleccionar algo.
   * @default "Select a value"
   */
  defaultLabel?: string;
  size?: DropDownSize;
  /**
   * Tailwind width class (p.ej. "w-48") para
   * controlar el ancho del contenedor.
   * @default "w-full"
   */
  widthClass?: string;

  /**
   * Si es true, se deshabilita el dropdown.
   * @default false
   */
  disabled?: boolean;

  /**
   * Se llama cuando se selecciona un item.
   */
  onSelect?: (selectedItem: string) => void;

  /**
   * Si se provee, el componente funciona como "controlled"
   * y mostrará este `value` en vez de su estado interno.
   */
  value?: string;

  /**
   * Clases extra para el contenedor externo.
   */
  className?: string;
  /**
   * Prop to customize the icon
   */
  icon?: React.ReactNode;
  /**
   * API call function
   */
  fetchOptions?: (selectedItem: string) => void;
  /**
   * This is a prop to control the position of the text displayed
   */
  position?: "left" | "right";

  /**
   * Clase Tailwind opcional para establecer un min-width.
   * @default "min-w-[140px]"
   */
  minWidthClass?: string;

  /**
   * Clase Tailwind opcional para establecer un max-width.
   * @default "max-w-[200px]"
   */
  maxWidthClass?: string;
  /** Max width for the dropdown content (when using icon-only) */
  dropdownMaxWidthClass?: string;
  /** Subtitle to show in the dropdown content for icon-only dropdowns */
  dropdownSubtitle?: string;
  /** Custom message to display when no items are available */
  emptyItemsMessage?: string;
  /** Property add an icon when no items are available */
  emptyIcon?: React.ReactNode;
  /** Property key to use for displaying labels from objects. */
  labelKey?: string;
  /** Props for the Badge */
  badgeProps?: Omit<BadgeProps, "children">;
  /** Tabs inside the dropdown */
  tabs?: SegmentedControlProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderItem?: (item: any) => React.ReactNode;
  buttonProps?: GenericButtonProps;
  /** Whether to use fixed positioning for the dropdown menu */
  fixedPosition?: boolean;
  children?: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
  autoOpenOnBadgeClick?: boolean;
  dropdownMaxHeightClass?: string;
}

/** Pequeño ícono de flecha abajo. */
function ChevronDownIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 12a.997.997 0 01-.707-.293l-4-4
           a1 1 0 111.414-1.414L10 9.586
           l3.293-3.293a1 1 0 011.414
           1.414l-4 4A.997.997 0 0110
           12z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Dropdown genérico estilizado como input.
 */
const GenericDropdown: React.FC<GenericDropdownProps> = ({
  items,
  defaultLabel = "Select a value",
  size = "md",
  widthClass = "w-full",
  disabled = false,
  onSelect,
  value,
  className = "",
  icon,
  fetchOptions,
  position = "left",
  minWidthClass = "min-w-[140px]",
  maxWidthClass = "max-w-[200px]",
  dropdownMaxWidthClass,
  dropdownSubtitle,
  emptyItemsMessage,
  emptyIcon,
  labelKey = "nameDisplay",
  badgeProps,
  tabs,
  renderItem,
  buttonProps,
  fixedPosition = false,
  children,
  onOpenChange,
  dropdownMaxHeightClass
}) => {
  // Valor interno si el dropdown no es "controlled" externamente.
  const [internalSelected, setInternalSelected] = useState<string>(defaultLabel);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<string | number>(tabs?.value ?? "0");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to get the display label from an item.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getItemLabel = (item: any): string => {
    if (!item) return "";
    if (typeof item === "object") {
      return item[labelKey] ? String(item[labelKey]) : String(item);
    }
    return String(item);
  };

  // This is for an icon and no default label
  const iconOnlyMode = Boolean(icon && defaultLabel === "");
  // Si items.length === 0, mostramos "No options available" en el botón.
  const displayedValue =
  items.length === 0
    ? "No options available"
    : value !== undefined
    ? value === ""
      ? defaultLabel
      : iconOnlyMode
        ? ""
        : getItemLabel(value)
    : iconOnlyMode
      ? ""
      : getItemLabel(internalSelected);

  // Cerrar menú cuando se hace click fuera del dropdown.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update dropdown position when it opens
  useEffect(() => {
    if (isOpen && fixedPosition && dropdownRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + window.scrollY,
        left: buttonRect.left + window.scrollX,
        width: buttonRect.width,
      });
    }
  }, [isOpen, fixedPosition]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen((prev) => !prev);
    }
  };

  const handleSelect = async (option: string) => {
    // Si NO es controlled, guardamos en el estado interno.
    // const handleSelect = (option: string) => {
    // Solo actualizamos el estado interno si no es "controlled".
    if (value === undefined) {
      setInternalSelected(option);
    }
    if (fetchOptions) {
      await fetchOptions(option);
    }
    onSelect?.(option);
    setIsOpen(false);
    onOpenChange?.(false);
  };

  // This is added for the iconOnly cases width
    const buttonWidthClass = iconOnlyMode
      ? "w-auto"
      : `${widthClass} ${minWidthClass} ${maxWidthClass}`;

    const containerWidthClass = iconOnlyMode ? "w-200" : widthClass;
    const containerMaxWidth = iconOnlyMode && dropdownMaxWidthClass ? dropdownMaxWidthClass : maxWidthClass;    
    
    // Here we use the sizeClasses mapping to determine size-specific classes.
    const triggerButtonSizeClass = sizeClasses[size];

  // The content to show inside the trigger button
  const triggerContent = (
    <div className="flex items-center justify-between w-full">
      {!iconOnlyMode && displayedValue !== "" && <span>{displayedValue}</span>}
      {icon || <ChevronDownIcon className="ml-2 text-semantic-text-secondary" />}
    </div>
  );
  const triggerButton = buttonProps ? (
    <div className={`${buttonWidthClass}`}>
      <GenericButton
        {...buttonProps}
        onClick={toggleDropdown}
        disabled={disabled}
        style={{ width: "100%", ...(buttonProps.style || {}) }}
      >
        {triggerContent}
      </GenericButton>
    </div>
  ) : (
    <button
      onClick={toggleDropdown}
      disabled={disabled}
      className={`
        ${buttonWidthClass} ${triggerButtonSizeClass}
        text-semantic-text-secondary bg-semantic-bg-primary-alt
        border border-semantic-border-secondary rounded-sm
        hover:bg-semantic-state-active transition-colors
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
    >
      {triggerContent}
    </button>
  );

  // Optionally wrap the trigger button with GenericBadge if badgeProps exists
  const triggerWithBadge = badgeProps ? (
    <GenericBadge {...badgeProps}>{triggerButton}</GenericBadge>
  ) : (
    triggerButton
  );

    return (
      <div ref={dropdownRef} className={`relative ${className}`}>
        {/* Ensure children is passed properly to GenericBadge */}
        {triggerWithBadge}
        {isOpen && (
        <div
          className={`
            ${!fixedPosition ? 'absolute' : 'fixed'} 
            ${!fixedPosition ? 'mt-1' : ''}
            ${containerWidthClass} ${minWidthClass} ${containerMaxWidth}
            bg-semantic-bg-primary-alt
            border border-semantic-border-secondary
            rounded-sm
            shadow-md
            z-50
            ${!fixedPosition ? (position === "left" ? "left-0" : "right-0") : ''}
          `}
          style={fixedPosition ? {
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
          } : undefined}
        >
          {iconOnlyMode && dropdownSubtitle && (
            <div className="px-3 py-2 border-b border-semantic-border-secondary">
              <span className="text-sm font-medium text-semantic-text-secondary">
                {dropdownSubtitle}
              </span>
            </div>
          )}
          {tabs && (
            <SegmentedControl
              {...tabs}
              value={selectedTab}
              onChange={(val) => setSelectedTab(val)}
              className="p-2"
              maxWidthClass="full"
            />
          )}
          <ul className={`py-1 ${fixedPosition ? 'z-[999]' : ''} ${dropdownMaxHeightClass ?? ''} overflow-y-auto`}>
            {items.length === 0 ? (
              <li>
                <div className="flex items-center justify-center px-3 py-2 text-sm text-semantic-text-secondary cursor-default">
                  {emptyIcon && <span className="mr-1">{emptyIcon}</span>}
                  <span>{emptyItemsMessage || "No options available"}</span>
                </div>
              </li>
            ) : (
              items.map((item) => (
                <li key={item}>
                  {renderItem ? (
                    renderItem(item)
                  ) : (
                    <button
                      onClick={() => handleSelect(item)}
                      className="
                        w-full text-left
                        px-3 py-2
                        text-sm
                        text-semantic-text-secondary
                        hover:bg-semantic-state-active
                        hover:text-semantic-text-brand
                        transition-colors
                      "
                    >
                      {getItemLabel(item)}
                    </button>
                  )}
                </li>
              ))
            )}
          </ul>
          {children}
        </div>
      )}
      </div>
    );
    
};

export default GenericDropdown;
